import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { scheduleUndoableDelete } from "@/lib/undoDelete";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, PencilSimple, UserCircle, Trash, MagnifyingGlass } from "@phosphor-icons/react";

export default function Customers() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nama: "", telepon: "", alamat: "", keterangan: "", aktif: true });

  const load = useCallback(async () => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    const { data } = await api.get("/customers", { params });
    setItems(data);
  }, [q]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 400);
    return () => clearTimeout(t);
  }, [q, load]);

  const openNew = () => {
    setEditing(null);
    setForm({ nama: "", telepon: "", alamat: "", keterangan: "", aktif: true });
    setOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      nama: c.nama || "",
      telepon: c.telepon || "",
      alamat: c.alamat || "",
      keterangan: c.keterangan || "",
      aktif: c.aktif !== false,
    });
    setOpen(true);
  };

  const onDelete = (c) => {
    scheduleUndoableDelete({
      items, setItems, target: c,
      label: `Pelanggan "${c.nama}"`,
      apiCall: () => api.delete(`/customers/${c.id}`, { params: { hard: true } }),
      onDone: load,
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/customers/${editing.id}`, form);
        toast.success("Pelanggan diperbarui");
      } else {
        await api.post("/customers", form);
        toast.success("Pelanggan ditambahkan");
      }
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="p-6 md:p-8 max-w-5xl">
      <div className="flex flex-col md:flex-row md:items-end justify-between border-b border-border pb-6 mb-6 gap-4">
        <div>
          <div className="overline">Master Data</div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2 flex items-center gap-3">
            <span className="w-11 h-11 rounded-full bg-orange-100 text-primary flex items-center justify-center shrink-0">
              <UserCircle size={22} weight="bold" />
            </span>
            Pelanggan
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Daftar penerima/tujuan pengiriman. Dipakai sebagai autocomplete saat membuat SPB.
          </p>
        </div>
        <Button onClick={openNew} className="rounded-lg font-bold uppercase tracking-wider" data-testid="add-customer-button">
          <Plus size={16} className="mr-2" /> Tambah Pelanggan
        </Button>
      </div>

      <div className="relative mb-6 max-w-md">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cari nama / telepon / alamat..."
          className="rounded-lg h-11 pl-10"
          data-testid="customer-search-input"
        />
      </div>

      <div className="border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-secondary/50">
            <tr className="text-left">
              <th className="p-3 overline">Nama</th>
              <th className="p-3 overline">Telepon</th>
              <th className="p-3 overline">Alamat</th>
              <th className="p-3 overline">Status</th>
              <th className="p-3 overline text-right">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">Belum ada pelanggan. Tambahkan yang pertama.</td></tr>
            ) : items.map((c) => (
              <tr key={c.id} className="border-b border-border hover:bg-secondary/50" data-testid={`customer-row-${c.id}`}>
                <td className="p-3 font-bold" data-testid={`customer-nama-${c.id}`}>
                  {c.nama}
                  {c.auto_added_from_spb && (
                    <span className="ml-2 inline-block border border-blue-400/60 bg-blue-50 text-blue-800 text-[9px] font-bold uppercase tracking-wider px-1 py-0.5" title="Ditambah otomatis dari form SPB">
                      Auto
                    </span>
                  )}
                </td>
                <td className="p-3 font-mono text-xs">{c.telepon || <span className="text-muted-foreground">—</span>}</td>
                <td className="p-3 text-xs">{c.alamat || <span className="text-muted-foreground">—</span>}</td>
                <td className="p-3">
                  <span className={`status-pill inline-flex ${c.aktif ? "status-termuat" : "status-belum"}`}>
                    {c.aktif ? "AKTIF" : "NONAKTIF"}
                  </span>
                </td>
                <td className="p-3">
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" className="rounded-lg" onClick={() => openEdit(c)} data-testid={`edit-customer-${c.id}`}>
                      <PencilSimple size={14} className="mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-lg text-destructive bg-destructive/5 border-destructive/40"
                      onClick={() => onDelete(c)}
                      data-testid={`delete-customer-${c.id}`}
                      title="Hapus permanen">
                      <Trash size={14} weight="fill" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-lg max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Pelanggan" : "Tambah Pelanggan"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="customer-form">
            <div>
              <Label className="overline block mb-2">Nama Pelanggan *</Label>
              <Input value={form.nama} onChange={(e) => setForm((f) => ({ ...f, nama: e.target.value }))} required className="rounded-lg h-11" data-testid="input-customer-nama" />
            </div>
            <div>
              <Label className="overline block mb-2">Telepon</Label>
              <Input value={form.telepon} onChange={(e) => setForm((f) => ({ ...f, telepon: e.target.value }))} className="rounded-lg h-11 font-mono" data-testid="input-customer-telepon" placeholder="0812-3456-7890" />
            </div>
            <div>
              <Label className="overline block mb-2">Alamat</Label>
              <Input value={form.alamat} onChange={(e) => setForm((f) => ({ ...f, alamat: e.target.value }))} className="rounded-lg h-11" data-testid="input-customer-alamat" placeholder="Jl. Merdeka No.1, Medan" />
            </div>
            <div>
              <Label className="overline block mb-2">Keterangan (opsional)</Label>
              <Input value={form.keterangan} onChange={(e) => setForm((f) => ({ ...f, keterangan: e.target.value }))} className="rounded-lg h-11" data-testid="input-customer-keterangan" />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.aktif} onCheckedChange={(v) => setForm((f) => ({ ...f, aktif: v }))} />
              <Label>Aktif</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-lg" onClick={() => setOpen(false)}>Batal</Button>
              <Button type="submit" className="rounded-lg font-bold uppercase" data-testid="save-customer-button">Simpan</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
